import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { ChevronDown, ChevronRight, ThumbsDown, ThumbsUp } from 'lucide-react';
import { toolCalls, type TurnView } from '../../Helpers/assistantChatView';

const STOP_REASON_LABELS: Record<string, string> = {
    end_turn: 'Klart',
    max_tokens: 'Avbrutet (för långt svar)',
    refusal: 'Avböjt',
    limit_reached: 'Gräns för verktygsanrop nådd',
    error: 'Fel',
};

interface Props {
    turn: TurnView;
    /** Resolves to true when saved. */
    onFeedback: (turnKey: string, rating: 'up' | 'down', comment?: string) => Promise<boolean>;
}

/** Feedback and diagnostics under one answer (the chats are kept for diagnosing behaviour). */
const AssistantTurnFooter: React.FC<Props> = ({ turn, onFeedback }) => {
    const [rating, setRating] = useState<'up' | 'down' | null>(null);
    const [comment, setComment] = useState('');
    const [saving, setSaving] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const saved = turn.feedback;
    const d = turn.diagnostics;
    const calls = toolCalls(turn);

    const save = async () => {
        if (!rating) return;
        setSaving(true);
        const ok = await onFeedback(turn.key, rating, comment.trim() || undefined);
        setSaving(false);
        if (ok) {
            setRating(null);
            setComment('');
        }
    };

    return (
        <div className="assistant-turn-footer">
            <div className="assistant-turn-actions">
                {saved ? (
                    <span className="assistant-feedback-saved">
                        {saved.rating === 'up' ? <ThumbsUp size={14} /> : <ThumbsDown size={14} />} Omdöme sparat
                    </span>
                ) : (
                    <>
                        <button type="button" className={`assistant-icon-button ${rating === 'up' ? 'active' : ''}`} aria-label="Bra svar" aria-pressed={rating === 'up'} onClick={() => setRating('up')}>
                            <ThumbsUp size={16} />
                        </button>
                        <button type="button" className={`assistant-icon-button ${rating === 'down' ? 'active' : ''}`} aria-label="Dåligt svar" aria-pressed={rating === 'down'} onClick={() => setRating('down')}>
                            <ThumbsDown size={16} />
                        </button>
                    </>
                )}
                <button type="button" className="assistant-details-toggle" aria-expanded={showDetails} onClick={() => setShowDetails(!showDetails)}>
                    {showDetails ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Detaljer
                </button>
            </div>

            {rating && !saved && (
                <div className="assistant-feedback-form">
                    <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Kommentar (valfritt)"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        maxLength={2000}
                    />
                    <div className="assistant-feedback-buttons">
                        <Button size="sm" variant="outline-secondary" onClick={() => setRating(null)} disabled={saving}>Avbryt</Button>
                        <Button size="sm" onClick={save} disabled={saving}>{saving ? 'Sparar…' : 'Spara omdöme'}</Button>
                    </div>
                </div>
            )}

            {saved?.comment && <div className="assistant-feedback-comment">”{saved.comment}”</div>}

            {showDetails && (
                <dl className="assistant-diagnostics">
                    <dt>Status</dt><dd>{STOP_REASON_LABELS[d.stopReason] ?? d.stopReason}{d.error ? `: ${d.error}` : ''}</dd>
                    <dt>Tid</dt><dd>{(d.durationMs / 1000).toFixed(1)} s</dd>
                    <dt>Kostnad</dt><dd>≈ {d.costEstimateSek.toFixed(2)} kr</dd>
                    <dt>Tokens</dt><dd>in {d.inputTokens} · ut {d.outputTokens} · cache {d.cacheReadTokens}/{d.cacheWriteTokens}</dd>
                    <dt>Modell</dt><dd>{d.model}</dd>
                    {calls.length > 0 && (
                        <>
                            <dt>Verktyg</dt>
                            <dd>
                                <ol className="assistant-tool-calls">
                                    {calls.map((call, i) => (
                                        <li key={i} className={call.error ? 'assistant-tool-call--error' : undefined}>
                                            <code>{call.name}</code> {call.input} · {call.durationMs} ms · {call.resultChars} tecken
                                            {call.error && <> · {call.error}</>}
                                        </li>
                                    ))}
                                </ol>
                            </dd>
                        </>
                    )}
                </dl>
            )}
        </div>
    );
};

export default AssistantTurnFooter;
